import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { keyframes } from 'styled-components';

import ErrorBoundary from 'components/Common/ErrorBoundary';
import { CANVAS_ITEM_MARGIN } from 'components/ReportCanvas/canvasConstants';
import ChartLegendLinkContext from 'components/StudioCharts/ChartLegendLinkContext';
import ChartOptionsTransformContext from 'contexts/ChartOptionsTransformContext';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

import {
  MAX_ELEMENTS_PER_ROW,
  RowSizing,
  findElement,
  getCardMetricIds,
  getElementMetricIds,
  getRowScale,
  getRowSizing,
  isNumberElement,
  isRowStretched,
  isRowTight,
  isTextElement,
  duplicateElement,
  moveElement,
  moveRowBefore,
  removeElement,
  resizeRowHeight,
  rowHasChart,
  getRowsScale,
  growRowsToFit,
  setElementLayouts,
  setRowHeight,
  updateElement,
} from '../compositeUtils';
import {
  DEFAULT_ACCENT,
  NO_DRAG_CLASS,
  COMPOSITE_NEW_ROW_SLOT_HEIGHT,
  STUDIO_ADD_ROW_CARD_ATTR,
  STUDIO_COMPOSITE_CARD_ATTR,
  STUDIO_ROW_ID_ATTR,
  STUDIO_ELEMENT_ID_ATTR,
  toAccentColor,
  toAccentCss,
} from '../constants';
import { DateScope } from '../dateSettings';
import DragChip, { DRAG_CHIP_CLASS, DRAG_CHIP_REVEALED_CSS } from '../DragChip';
import { FilterScope } from '../filterSettings';
import {
  CardActions,
  CardHeader,
  CardShell,
  OverlayActions,
  OverlayViewMenu,
  SHELL_PADDING,
} from '../CardChrome';
import { BlockMenuScope } from '../BlockMenuItems';
import { isEntityDetailsTable } from '../buildEntityInformationTableDefinition';
import { isGroupVsRemainderLine } from '../buildGroupVsRemainderDefinition';
import { isLegacyMatrixTable } from '../buildLegacyMatrixDefinition';
import { isMetricListTable } from '../buildMetricListDefinition';
import { isKpiListTable } from '../kpiList';
import { isRemindersTable } from '../buildRemindersDefinition';
import { takesNoMetrics } from '../legacyGadgetBlocks';
import SetupPlaceholder from '../SetupPlaceholder';
import StudioChart from '../StudioChart';
import StudioEntityDetails from '../StudioEntityDetails';
import StudioGauge from '../StudioGauge';
import StudioGroupVsRemainder from '../StudioGroupVsRemainder';
import StudioKpiList from '../StudioKpiList';
import StudioLegacyMatrix from '../StudioLegacyMatrix';
import StudioMetricList from '../StudioMetricList';
import StudioReminders from '../StudioReminders';
import { useStudioChartDrillDown } from '../StudioChartMenu';
import useElementSize from '../useElementSize';
import { DynamicStudioText } from '../StudioText';
import {
  CompositeLegendProvider,
  useLegendChartLink,
  useLegendChartTransform,
  useLegendNumber,
} from './compositeLegend';
import { CompositeChartPopup } from './compositeChartPopup';
import ElementMenu, {
  ELEMENT_MENU_CLASS,
  ElementEditActions,
} from './ElementMenu';
import NumberElement from './NumberElement';
import CellResizeHandles, {
  RESIZE_EDGES,
  RESIZE_HANDLE_CLASS,
  RESIZE_HANDLES_CLASS,
  ResizePreview,
  useCompositeResize,
} from './ResizeHandle';
import RowHandle, { ROW_HANDLE_CLASS } from './RowHandle';
import RowResizeHandle, { ROW_RESIZE_HANDLE_CLASS } from './RowResizeHandle';
import RowToolbar, { ROW_TOOLBAR_CLASS } from './RowToolbar';
import useElementTitle from './useElementTitle';

// Rows space themselves (not a flex gap) so a divided row's borders and
// dividers can run into the rows around it
// Right under the header, whose padding spaces them from the title. With the
// header hidden, a divided first row runs into the block's top edge (as a
// divided last row does its bottom) rather than doubling its border.
const Rows = styled.div<{ $isHeaderHidden: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;

  ${(props) =>
    props.$isHeaderHidden
      ? `
    > :first-child {
      border-top: none;
    }
  `
      : ''}
`;

// The hover badges' classes, revealed by the row's and cell's :hover rules
const ROW_BADGE_CLASS = 'studio-composite-row-badge';
// A row's and a cell's classes, so a hovered row can outline its cells
const ROW_CLASS = 'studio-composite-row';
const ELEMENT_CELL_CLASS = 'studio-composite-cell';
// How far in from its sides a plain row's hover outline is drawn, and its
// cells' outlines further in, so neither runs along the block's edge. Only
// drawn in: nothing moves. Divided rows run edge to edge, outlines and all.
const ROW_OUTLINE_INSET_X = 4;
const CELL_OUTLINE_INSET_X = 6;
// How far a block's tabs sit in from its corner on the canvas (see
// StudioCanvas's SelectedChip), which its rows' and cells' match
const CANVAS_TAB_INSET_X = 12;
// Between a plain row's cells
const ROW_GAP = 24;
// Around a plain row's cells in Studio, selected or not (so selecting the
// block doesn't move them), leaving room for the outlines it then draws. The
// sides' leave as much between a cell's outline and its row's (drawn
// ROW_OUTLINE_INSET_X in) as above and below.
const ROW_STUDIO_PADDING_Y = 14;
const ROW_STUDIO_PADDING_X = 12;
// A row resized by its bottom edge goes in steps of this many grid rows (as
// its children's edges do; see useCompositeResize), down to its least height
const ROW_RESIZE_GRID_ROWS = 2;
const ROW_MIN_PX = 32;
const CHART_ROW_MIN_PX = 80;
// Where a dragged row would go, above or below the row it's over
const ROW_DROP_BAR_CLASS = 'studio-composite-row-drop-bar';
// What a row holds besides its cells: its toolbar, badge, resize handle and
// drop bar
const NOT_ROW_CHROME = [
  ROW_TOOLBAR_CLASS,
  ROW_BADGE_CLASS,
  ROW_RESIZE_HANDLE_CLASS,
  ROW_HANDLE_CLASS,
  ROW_DROP_BAR_CLASS,
]
  .map((c) => `:not(.${c})`)
  .join('');
const ELEMENT_BADGE_CLASS = 'studio-composite-element-badge';
// A cell's top right corner is its ⋮ menu's (see ElementMenu), so it has no
// resize handle there: its top and right edges resize it
const CELL_RESIZE_EDGES = RESIZE_EDGES.filter((edge) => edge !== 'ne');

// Names a hovered row ("Row 1"), or marks the selected row or cell as the one
// being edited ("Editing"), on a tab above its outline's corner, like the
// canvas's tab. In the block's first row
// with its header hidden there's no room above (the block clips it), so it
// hangs inside the corner instead.
// While an element is dragged (or a sidebar metric or vis type that could
// be one), a small dashed shape with a + in each gap it could go in: before
// each element, and after a row's last. Centred in the gap between elements;
// at a row's ends, just outside them. They show as the drag comes over the
// block. The one it would go in now is filled, and says where (see
// DropSpotLabel); for a sidebar drag, the preview of the gadget there stands
// in for it (see DropPreviewCell).
const DROP_SPOT_WIDTH = 12;
const DropSpotBar = styled.div<{
  $side: DropSide;
  $offset: number;
  $isActive: boolean;
  $isHidden: boolean;
}>`
  position: absolute;
  top: 4px;
  bottom: 4px;
  ${(props) => (props.$side === 'before' ? 'left' : 'right')}: ${(props) =>
    `calc(-${props.$offset}px - ${DROP_SPOT_WIDTH / 2}px)`};
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${DROP_SPOT_WIDTH}px;
  border-radius: 5px;
  border: 1.5px dashed var(--color-studio-selection);
  background-color: ${(props) =>
    props.$isActive
      ? 'var(--color-studio-drop-spot-bg-active)'
      : 'var(--color-studio-drop-spot-bg)'};
  color: var(--color-studio-drop-spot-icon);
  opacity: ${(props) => (props.$isHidden ? 0 : 1)};
  pointer-events: none;
`;

// Where a sidebar drag would land in a row, as the canvas previews a drop
// (see GridBackdrop): a dashed, haloed cell of the gadget's width there (a
// Number's natural width, else its share of the row), the cells around it
// easing aside as it grows in. Moved on, it shrinks back out; the gap beside
// it closes up with it (a negative margin as wide, on the side given by
// --collapse-left / --collapse-right), so its neighbours slide rather than
// jump by the gap. Where the drag goes is worked out from where the cells
// were before it opened (see compositeDropTarget), so it can't move itself.
const DROP_PREVIEW_MS = 180;
const DROP_PREVIEW_NUMBER_WIDTH = 140;
const shrunkPreview = `
  opacity: 0;
  margin-left: var(--collapse-left, 0);
  margin-right: var(--collapse-right, 0);
`;
const dropPreviewFillIn = keyframes`
  from {
    flex-grow: 0;
    ${shrunkPreview}
  }
`;
const dropPreviewFixedIn = keyframes`
  from {
    flex-basis: 0;
    ${shrunkPreview}
  }
`;
const dropPreviewFillOut = keyframes`
  to {
    flex-grow: 0;
    ${shrunkPreview}
  }
`;
const dropPreviewFixedOut = keyframes`
  to {
    flex-basis: 0;
    ${shrunkPreview}
  }
`;
const DropPreviewCell = styled.div<{ $isFill: boolean; $isClosing: boolean }>`
  position: relative;
  z-index: 3;
  flex: ${(props) =>
    props.$isFill ? '1 1 0' : `0 1 ${DROP_PREVIEW_NUMBER_WIDTH}px`};
  align-self: stretch;
  min-width: 0;
  min-height: 48px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1.5px dashed var(--color-studio-drop-preview-border);
  background-color: var(--color-studio-drop-preview-bg);
  box-shadow: 0 0 0 4px var(--color-studio-drop-preview-halo);
  color: var(--color-studio-drop-spot-icon);
  pointer-events: none;
  animation: ${(props) =>
      props.$isClosing
        ? props.$isFill
          ? dropPreviewFillOut
          : dropPreviewFixedOut
        : props.$isFill
          ? dropPreviewFillIn
          : dropPreviewFixedIn}
    ${DROP_PREVIEW_MS}ms
    ${(props) => (props.$isClosing ? 'ease-in forwards' : 'ease-out')};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    ${(props) => (props.$isClosing ? 'display: none;' : '')}
  }
`;

// Its +, clipped while it's narrow
const DropPreviewIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow: hidden;
`;

// Over the spot a drag would go in (or the new row slot), what dropping there
// does, as the hover badges name a row or cell. Pops in with the spot's fill.
const dropSpotLabelIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 4px);
  }
`;
const DropSpotLabel = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--color-studio-selection);
  color: var(--color-text-inverse);
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  white-space: nowrap;
  pointer-events: none;
  animation: ${dropSpotLabelIn} 150ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// The new row slot's top edge (see FloatingAddRowSlot), for its label to sit
// on
const NewRowLabelAnchor = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 4;
  height: 0;
  pointer-events: none;
`;

// Across the row's top or bottom edge, where a dragged row would go
const RowDropBar = styled.div<{ $side: DropSide; $inset: number }>`
  position: absolute;
  ${(props) => (props.$side === 'before' ? 'top' : 'bottom')}: -2px;
  left: ${(props) => props.$inset}px;
  right: ${(props) => props.$inset}px;
  z-index: 4;
  height: 3px;
  border-radius: 2px;
  background-color: var(--color-studio-selection);
  pointer-events: none;
`;

const HoverBadge = styled.div<{ $isInside: boolean; $left: number }>`
  position: absolute;
  ${(props) => (props.$isInside ? 'top: 0;' : 'bottom: 100%;')}
  left: ${(props) => props.$left}px;
  z-index: 3;
  padding: 1px 6px;
  border-radius: ${(props) =>
    props.$isInside ? '0 0 4px 4px' : '4px 4px 0 0'};
  background: var(--color-studio-selection);
  color: var(--color-text-inverse);
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.1s ease;
`;

const ROW_FLEX: Record<RowSizing['mode'], string> = {
  share: '0 1 auto',
  fill: '1 1 0',
  content: 'none',
  grow: '1 1 auto',
};

// A row takes its share of the rows' area as getRowSizing says: a set
// height, chart rows the rest, Number and Text rows what they need (or the
// rest, with no chart row to take it). Numbers and Text never shrink below
// what they need, so a set height on a chart row is the most it takes: it
// gives way to the Numbers around it, down to a chart's least. Divided rows
// are edge-to-edge strips with borders above and below and dividers running
// between them. A divided last row is pinned to the bottom and runs into the
// block's edge instead of having a bottom border.
const Row = styled.div<{
  $sizing: RowSizing;
  $hasChart: boolean;
  $hasDividers: boolean;
  $isHoverable: boolean;
  // In Studio: padded for its outlines (see ROW_STUDIO_PADDING_Y)
  $isInStudio: boolean;
  // The block is selected: its rows and cells are outlined, faintly
  $isOutlined: boolean;
  // The row itself is selected (its settings open): outlined solid and named
  $isRowSelected: boolean;
  // A gadget dragged into the block would land in it
  $isDropTarget: boolean;
}>`
  position: relative;
  display: flex;
  flex: ${(props) => ROW_FLEX[props.$sizing.mode]};
  ${(props) =>
    props.$sizing.mode === 'share' ? `height: ${props.$sizing.percent}%;` : ''}
  gap: ${(props) => (props.$hasDividers ? 0 : ROW_GAP)}px;
  min-height: ${(props) =>
    props.$hasChart ? `${CHART_ROW_MIN_PX}px` : 'auto'};

  /* Its handles show while it's hovered (or selected), its cells or not:
     a divided row's cells leave it no space of its own */
  > .${ROW_RESIZE_HANDLE_CLASS}, > .${ROW_HANDLE_CLASS} {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s ease;
  }

  &:hover > .${ROW_RESIZE_HANDLE_CLASS}, &:hover > .${ROW_HANDLE_CLASS} {
    opacity: 1;
    pointer-events: auto;
  }

  /* Dragged by its body to reorder it (see rowDragHandlersFor) */
  &[draggable='true'] {
    cursor: grab;
  }

  /* Editing in Studio, a hovered row is outlined (dashed, as a cell's is
     solid) and named. A hovered cell names itself, with its row. In a
     selected block, every row and cell is faintly outlined, showing its
     layout (a divided row's borders and dividers already do). */
  ${(props) =>
    props.$isHoverable
      ? `
    /* Its tint, under its cells (positioned, they paint over it), so they
       stay as they are */
    &::before {
      content: '';
      position: absolute;
      inset: 0 ${props.$hasDividers ? 0 : ROW_OUTLINE_INSET_X}px;
      border-radius: ${props.$hasDividers ? 0 : 6}px;
      pointer-events: none;
    }

    &:hover::before {
      background-color: var(--color-studio-row-hover-bg);
    }

    /* Its outline, over them */
    &::after {
      content: '';
      position: absolute;
      inset: 0 ${props.$hasDividers ? 0 : ROW_OUTLINE_INSET_X}px;
      z-index: 1;
      border: 1px dashed
        ${
          props.$isOutlined && !props.$hasDividers
            ? 'var(--color-studio-cell-outline)'
            : 'transparent'
        };
      border-radius: ${props.$hasDividers ? 0 : 6}px;
      pointer-events: none;
    }

    &:hover::after {
      border-color: var(--color-studio-selection);
    }

    &:hover > .${ROW_BADGE_CLASS} {
      opacity: 1;
    }

    &:has(:hover > .${ELEMENT_BADGE_CLASS}) > .${ROW_BADGE_CLASS} {
      opacity: 0;
    }

    /* Its drag tab, unless one of its cells is hovered (which shows its own) */
    &:hover:not(:has(> .${ELEMENT_CELL_CLASS}:hover))
      > .${DRAG_CHIP_CLASS}
      > * {
      ${DRAG_CHIP_REVEALED_CSS}
    }
  `
      : ''}

  ${(props) =>
    props.$isHoverable && props.$isRowSelected
      ? `
    &::after,
    &:hover::after {
      border: 1px solid var(--color-studio-selection);
    }

    &::before {
      background-color: var(--color-studio-row-hover-bg);
    }

    > .${ROW_BADGE_CLASS},
    &:has(:hover > .${ELEMENT_BADGE_CLASS}) > .${ROW_BADGE_CLASS} {
      opacity: 1;
    }

    > .${ROW_RESIZE_HANDLE_CLASS},
    > .${ROW_HANDLE_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }

    /* Its Delete has the corner their menus float on */
    .${ELEMENT_MENU_CLASS} {
      display: none;
    }
  `
      : ''}

  /* Where a dragged gadget would land: outlined in the selection's colour,
     haloed and tinted, selected or not (a drag doesn't hover it) */
  ${(props) =>
    props.$isDropTarget
      ? `
    &::before,
    &::after {
      content: '';
      position: absolute;
      inset: 0 ${props.$hasDividers ? 0 : ROW_OUTLINE_INSET_X}px;
      border-radius: ${props.$hasDividers ? 0 : 6}px;
      pointer-events: none;
    }

    &::before {
      background-color: var(--color-studio-drop-preview-bg);
    }

    &::after,
    &:hover::after {
      z-index: 1;
      border: 1.5px solid var(--color-studio-selection);
      box-shadow: 0 0 0 3px var(--color-studio-drop-preview-halo);
    }
  `
      : ''}

  ${(props) =>
    props.$hasDividers
      ? `
    border-top: 1px solid var(--color-studio-cell-outline);
    border-bottom: 1px solid var(--color-studio-cell-outline);

    /* Cells only: the row's chrome (and a "+" slot) are its children too */
    > ${NOT_ROW_CHROME} + ${NOT_ROW_CHROME} {
      border-left: 1px solid var(--color-studio-cell-outline);
    }

    /* Consecutive strips share a border */
    & + & {
      border-top: none;
    }

    &:last-child {
      margin-top: auto;
      border-bottom: none;

      > ${NOT_ROW_CHROME} {
        padding-bottom: ${6 + SHELL_PADDING.y / 2}px;
      }
    }
  `
      : `
    /* More room around its cells in Studio, so their outlines sit clear of
       their row's */
    padding: ${
      props.$isInStudio
        ? `${ROW_STUDIO_PADDING_Y}px ${ROW_STUDIO_PADDING_X}px`
        : '4px 0'
    };
  `}
`;

// A cell's size and place in its row: a share of the row's width (less its
// share of the gaps between cells) and of the free space before it, and its
// own height (px) and the space above it, else it fills its row
type CellLayout = {
  widthPercent?: number;
  gapShare: number;
  offsetPercent?: number;
  heightPx?: number;
  offsetTopPx?: number;
};

// An element with a set width takes that share of the row, less its share
// of the gaps (shrinking if the shares add up to more). Otherwise Numbers in a plain row sit at
// their natural width (like a headline strip), and charts and divided cells
// split the rest evenly. The selection ring is an overlay so a chart's
// absolutely-positioned body can't paint over it. Cells in a divided row are
// square so the dividers run straight. Charts run to the cell's edges (they
// keep their own margins); Numbers and Text are inset to line up with the
// block's title. While an element is dragged, a bar on one side shows where
// it would go; in a selected block it's dragged by its body. Editing
// in Studio (isHoverable), a hovered cell that isn't selected is tinted and
// faintly outlined, so each reads as something to click.
const Element = styled.div<{
  $isFill: boolean;
  $isDivided: boolean;
  $isChart: boolean;
  $isSelected: boolean;
  $isHoverable?: boolean;
  $isOutlined?: boolean;
  $isHighlighted?: boolean;
  $layout?: CellLayout;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: ${(props) =>
    props.$layout && props.$layout.widthPercent !== undefined
      ? `0 1 calc(${props.$layout.widthPercent}% - ${props.$layout.gapShare}px)`
      : props.$isFill
        ? '1 1 0'
        : '0 1 auto'};
  ${(props) =>
    props.$layout && props.$layout.offsetPercent
      ? `margin-left: ${props.$layout.offsetPercent}%;`
      : ''}
  ${(props) =>
    props.$layout && props.$layout.heightPx !== undefined
      ? `
    align-self: flex-start;
    height: ${props.$layout.heightPx}px;
    margin-top: ${props.$layout.offsetTopPx ?? 0}px;
    /* Never past its row, should the row be squeezed shorter */
    max-height: calc(100% - ${props.$layout.offsetTopPx ?? 0}px);
  `
      : ''}
  gap: 2px;
  min-width: 0;
  min-height: 0;
  padding: ${(props) => (props.$isChart ? 0 : `6px ${SHELL_PADDING.x}px`)};
  border-radius: ${(props) => (props.$isDivided ? 0 : 6)}px;
  background-color: ${(props) =>
    props.$isHighlighted ? 'var(--color-bg-secondary)' : 'transparent'};
  transition: background-color 0.15s ease;

  /* Editing the layout, a plain row's cells are filled within their
     outlines (over their row's tint, and its outline: see Row), as the
     block is */
  ${(props) =>
    props.$isOutlined && !props.$isDivided
      ? `
    z-index: 2;

    &::before {
      content: '';
      position: absolute;
      inset: 0 ${CELL_OUTLINE_INSET_X}px;
      z-index: -1;
      border-radius: inherit;
      background-color: ${
        props.$isHighlighted
          ? 'var(--color-bg-secondary)'
          : 'var(--color-bg-raised)'
      };
      pointer-events: none;
      transition: background-color 0.15s ease;
    }
  `
      : ''}

  &::after {
    content: '';
    position: absolute;
    inset: 0 ${(props) => (props.$isDivided ? 0 : CELL_OUTLINE_INSET_X)}px;
    z-index: 1;
    border-radius: inherit;
    pointer-events: none;
    box-shadow: ${(props) =>
      props.$isSelected
        ? // As a selected block's ring is (see CardShell)
          'inset 0 0 0 2px var(--color-studio-selection)'
        : props.$isOutlined && !props.$isDivided
          ? 'inset 0 0 0 1px var(--color-studio-cell-outline)'
          : 'none'};
  }

  ${(props) =>
    props.$isHoverable && !props.$isSelected
      ? `
    /* As a hovered block on the canvas: outlined a touch more firmly and
       lifted with a soft shadow, untinted (nothing's shaded until it's
       selected) */
    &:hover::after {
      box-shadow:
        inset 0 0 0 1px var(--color-studio-selection-subtle),
        var(--shadow-dropdown);
    }
  `
      : ''}

  /* Selected in Studio, its block's children are dragged by their bodies */
  &[draggable='true'] {
    cursor: grab;
  }

  &:hover > .${ELEMENT_BADGE_CLASS} {
    opacity: 1;
  }

  /* Selected, it keeps its name showing (as a selected row does), hovered
     or not */
  ${(props) =>
    props.$isSelected
      ? `
    > .${ELEMENT_BADGE_CLASS} {
      opacity: 1;
    }
  `
      : ''}

  &:hover > .${DRAG_CHIP_CLASS} > * {
    ${DRAG_CHIP_REVEALED_CSS}
  }

  /* Its resize handles' marks, hinted at (see CellResizeHandles) */
  &:hover > .${RESIZE_HANDLES_CLASS} .${RESIZE_HANDLE_CLASS}::after {
    opacity: 1;
  }
`;

type DropSide = 'before' | 'after';
// A place an element could be dropped: beside an element of a row
type DropSpot = { elementId: string; side: DropSide };
const isSameSpot = (a: DropSpot, b: DropSpot | undefined): boolean =>
  !!b && a.elementId === b.elementId && a.side === b.side;

// Which side of a cell the pointer is on: dropped there, a dragged element
// goes before or after it
const getDropSide = (e: React.DragEvent<HTMLElement>): DropSide => {
  const rect = e.currentTarget.getBoundingClientRect();
  return e.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
};

// A strip under the block for a new row of it, shown while a sidebar metric
// or vis type is dragged over the block. It only takes a drop, lit while the
// drag is over it. Dragged between a row's elements, a drop goes in there
// instead (see addElementDrop).
const SLOT_LABEL = 'Drag in a gadget / metric to create a new row';
// Styled as the spots between the elements are (see DropSpotBar): dashed,
// filled, more so while the drag's over it, its + and label darker. Over
// whatever is beneath (outside the block), so its fill sits on the raised
// colour. Shown just below the block, so showing it moves nothing in it.
// Dropped on, the block grows a row at its bottom.
const FloatingAddRowSlot = styled.div<{ $isActive: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: ${COMPOSITE_NEW_ROW_SLOT_HEIGHT}px;
  overflow: hidden;
  box-sizing: border-box;
  border: 1.5px dashed var(--color-studio-selection);
  border-radius: 5px;
  background-image: linear-gradient(
    ${(props) =>
        props.$isActive
          ? 'var(--color-studio-drop-spot-bg-active)'
          : 'var(--color-studio-drop-spot-bg)'}
      0 0
  );
  background-color: var(--color-bg-raised);
  color: var(--color-studio-drop-spot-icon);
  font-size: 11px;
  font-weight: 500;
  line-height: 12px;
  white-space: nowrap;
`;

// A block without rows yet (as a new one starts), in Studio: an ask for
// gadgets filling it under its header. All of it takes a sidebar drag as its
// first row (as the "New row" slot does). Selected, it's outlined, dashed, as
// where gadgets go; blue while one's dragged that could go in it.
const EmptyLayout = styled.div<{
  $isHeaderHidden: boolean;
  $isOutlined: boolean;
  $isDropSpot: boolean;
  $isActive: boolean;
}>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${(props) => (props.$isHeaderHidden ? SHELL_PADDING.y : 0)}px
    ${SHELL_PADDING.x}px ${SHELL_PADDING.y}px;
  padding: 12px;
  border-radius: 8px;
  border: 1px dashed
    ${(props) =>
      props.$isActive || props.$isDropSpot
        ? 'var(--color-studio-selection)'
        : props.$isOutlined
          ? 'var(--color-studio-selection-subtle)'
          : 'transparent'};
  background-color: ${(props) =>
    props.$isActive
      ? 'var(--color-studio-drop-preview-halo)'
      : props.$isDropSpot
        ? 'var(--color-studio-drop-preview-bg)'
        : 'transparent'};
  transition:
    border-color 0.1s ease,
    background-color 0.1s ease;
`;

// A large Number's size as a share of the rows' height (44px, the largest,
// at about 490px)
const NUMBER_FONT_PER_HEIGHT = 0.09;

// Pressing the header selects the block itself rather than an element. With
// the header hidden, the element settings' back button (or Escape) does.
const HeaderHitArea = styled.div`
  flex-shrink: 0;
`;

// A chart element's title, over it when its header is shown. Inset to line
// up with the block's title; the chart takes the rest of the cell.
const ElementHeader = styled.div`
  flex-shrink: 0;
  min-width: 0;
  padding: 6px ${SHELL_PADDING.x}px 0;
`;

type MenuPlacement = {
  isInside: boolean;
  isAtBlockSide: boolean;
  insetX: number;
};

// Chart elements are titled by their label, else their primary metric
const ChartElement = ({
  element,
  w,
  h,
  isInteractive,
  hasContextMenu,
  isOnView,
  blockFilters,
  actions,
  editActions,
  isMenuShown,
  isSetupHintShown,
  menuPlacement,
}: {
  element: Studio.CompositeElement;
  w: number;
  h: number;
  isInteractive: boolean;
  // On a dashboard: the chart menu and popup, as a gadget's
  hasContextMenu: boolean;
  // On a dashboard or report, inside its gadget's providers: a viewer's
  // "Compare by" applies (see useRenderedComparison)
  isOnView: boolean;
  // Passed into its popup, with its own, as the popup's scope
  blockFilters: FilterPlateType[] | undefined;
  // The block's, while it's edited in Studio
  actions: CardActions | undefined;
  // Editing in Studio: its menu has what a block's does
  editActions: ElementEditActions | undefined;
  // Not in Studio until its block is selected
  isMenuShown: boolean;
  // Without metrics yet, its placeholder asks for it to be selected (in
  // Studio, while it isn't)
  isSetupHintShown: boolean;
  // Where its menu floats (see ElementMenu)
  menuPlacement: MenuPlacement;
}) => {
  const { title } = useElementTitle(element);
  // Linked to the block's Numbers, which may stand in for its legend: as a
  // Highcharts options transform, or as the link an ECharts chart draws
  const legendTransform = useLegendChartTransform(element);
  const legendLink = useLegendChartLink(element);
  const { drillDowns, filters, reportDrillDownId, boardDrillDownId } = element;
  const drillDownMenu = useStudioChartDrillDown({
    content: element,
    actions,
    elementId: element.id,
  });
  const popup = useMemo(
    (): CompositeChartPopup | undefined =>
      hasContextMenu && (reportDrillDownId || boardDrillDownId)
        ? {
            drillDowns: drillDowns ?? [],
            scope: [...(blockFilters ?? []), ...(filters ?? [])],
            reportDrillDownId,
            boardDrillDownId,
          }
        : undefined,
    [
      blockFilters,
      boardDrillDownId,
      drillDowns,
      filters,
      hasContextMenu,
      reportDrillDownId,
    ],
  );
  return (
    <BlockMenuScope content={element}>
      <>
        {/* Even on a dashboard: a Reminders gadget's own menu has its
            export, but an element has no gadget */}
        {isMenuShown && (
          <ElementMenu
            element={element}
            name={title}
            w={w}
            h={h}
            hasPopup={!!popup}
            editActions={editActions}
            {...menuPlacement}
          />
        )}
        {element.isHeaderShown && (
          <ElementHeader
            data-testid={`studio-composite-element-header-${element.id}`}
          >
            <Typography.Header type="H6" isEllipsis>
              {title}
            </Typography.Header>
          </ElementHeader>
        )}
        {/* Without metrics yet, the placeholder a gadget added to the canvas
            has (see EmptyBlock): a faded sample of its type. Not the kinds
            that draw without metrics, which aren't placeholders there
            either. */}
        {!takesNoMetrics(element) &&
        getElementMetricIds(element).length === 0 ? (
          <SetupPlaceholder
            visType={element.visType}
            isHintShown={isSetupHintShown}
            isHeaderHidden={!element.isHeaderShown}
            isElement
            testId={`studio-composite-element-placeholder-${element.id}`}
          />
        ) : (
          /* An element that fails to draw shows the classic gadget's error, with
            Retry, leaving the rest of the Composite drawn */
          <ErrorBoundary isRetryable>
            <>
              {element.visType === 'gauge' ? (
                <StudioGauge
                  content={element}
                  name={title}
                  isInteractive={isInteractive}
                />
              ) : isKpiListTable(element) ? (
                <StudioKpiList
                  content={element}
                  isInteractive={isInteractive}
                  isElement
                  isOnView={isOnView}
                />
              ) : isMetricListTable(element) ? (
                <StudioMetricList
                  content={element}
                  name={title}
                  isInteractive={isInteractive}
                />
              ) : isEntityDetailsTable(element) ? (
                <StudioEntityDetails
                  content={element}
                  name={title}
                  w={w}
                  h={h}
                  isInteractive={isInteractive}
                  isOnView={isOnView}
                />
              ) : isRemindersTable(element) ? (
                <StudioReminders
                  content={element}
                  name={title}
                  w={w}
                  isInteractive={isInteractive}
                  isOnView={isOnView}
                />
              ) : isGroupVsRemainderLine(element) ? (
                <StudioGroupVsRemainder
                  content={element}
                  name={title}
                  w={w}
                  h={h}
                  isInteractive={isInteractive}
                />
              ) : isLegacyMatrixTable(element) ? (
                <StudioLegacyMatrix
                  content={element}
                  name={title}
                  w={w}
                  h={h}
                  isInteractive={isInteractive}
                  isOnView={isOnView}
                />
              ) : (
                <ChartOptionsTransformContext.Provider value={legendTransform}>
                  <ChartLegendLinkContext.Provider value={legendLink}>
                    <StudioChart
                      content={element}
                      name={title}
                      w={w}
                      h={h}
                      isInteractive={isInteractive}
                      isOnView={isOnView}
                      hasContextMenu={hasContextMenu}
                      popup={popup}
                      drillDownMenu={drillDownMenu}
                    />
                  </ChartLegendLinkContext.Provider>
                </ChartOptionsTransformContext.Provider>
              )}
            </>
          </ErrorBoundary>
        )}
      </>
    </BlockMenuScope>
  );
};

// An element's cell. A Number linked to a chart's series is highlighted
// with it, from either side: its series always, and the Number's cell shaded
// too on a dashboard or report (isShadedWhenLinked), not in Studio, where
// hovering a cell shows nothing until it's selected.
const ElementCell = ({
  element,
  isShadedWhenLinked,
  children,
  ...cellProps
}: {
  element: Studio.CompositeElement;
  isShadedWhenLinked: boolean;
  children: React.ReactNode;
  $isFill: boolean;
  $isDivided: boolean;
  $isChart: boolean;
  $isSelected: boolean;
  $isHoverable: boolean;
  $isOutlined: boolean;
  $layout?: CellLayout;
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd?: () => void;
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
  'data-testid': string;
}) => {
  const link = useLegendNumber(
    isNumberElement(element) ? (element.metricIds ?? [])[0] : undefined,
  );
  return (
    <Element
      {...cellProps}
      className={ELEMENT_CELL_CLASS}
      $isHighlighted={isShadedWhenLinked && !!link?.isHighlighted}
      onMouseEnter={link ? () => link.setIsHovered(true) : undefined}
      onMouseLeave={link ? () => link.setIsHovered(false) : undefined}
      data-highlighted={link ? link.isHighlighted : undefined}
      {...{ [STUDIO_ELEMENT_ID_ATTR]: element.id }}
    >
      {children}
    </Element>
  );
};

// A block holding several elements (numbers, charts, text), stacked in rows
// under a header (unless it's hidden)
const CompositeCard = ({
  card,
  isSelected,
  isDropTarget,
  addElementDrop,
  valueFontSize,
  actions,
  selectedElementId,
  selectedRowId,
  selectOnRelease,
  onSelectElement,
  onSelectRow,
  onSelectBlock,
  onRowsChange,
  onDetach,
  onRowsGrow,
  isEmbedded = false,
  hasChartMenu = false,
  viewMenu,
}: {
  card: Studio.Card;
  isSelected: boolean;
  isDropTarget: boolean;
  // Set while a sidebar drag over the block could add an element: between
  // the elements of a row with room (a bar beside the indicator's element
  // shows where), or in a new row on its "New row" slot
  addElementDrop?: {
    rowId?: string;
    indicator?: { elementId: string; side: DropSide };
    isNewRow?: boolean;
    // What's dragged would be a Number, at its natural width (see
    // DropPreviewCell)
    isNumber?: boolean;
  };
  valueFontSize: number;
  // Absent in fullscreen, where the block is view-only: its elements can't be
  // selected or edited and its charts always take the pointer
  actions?: CardActions;
  selectedElementId: string | undefined;
  // Presses select on release, unless the block was dragged
  selectOnRelease: (apply: () => void) => void;
  onSelectElement: (elementId: string) => void;
  // The block's selected row, instead of an element in it
  selectedRowId?: string;
  // Selects a row, pressed in its own space between its elements (absent
  // where rows can't be selected, e.g. fullscreen)
  onSelectRow?: (rowId: string) => void;
  onSelectBlock: () => void;
  onRowsChange: (rows: Studio.CompositeRow[]) => void;
  // Takes an element or row out into a block of its own (on the canvas only)
  onDetach?: (target: { elementId?: string; rowId?: string }) => void;
  // Its rows resized taller than it has room for: it grows by gridRows to
  // fit them, pushing the blocks below down (on the canvas only). Without
  // it, they're scaled down to fit.
  onRowsGrow?: (grown: {
    rows: Studio.CompositeRow[];
    gridRows: number;
  }) => void;
  // On a dashboard or report, view-only like fullscreen
  isEmbedded?: boolean;
  // On a dashboard: clicking a chart's point opens the chart menu for it, as
  // a gadget's does
  hasChartMenu?: boolean;
  // On a dashboard or report: its menu, in the header or floated over the
  // corner when it's hidden
  viewMenu?: JSX.Element;
}) => {
  const isFullscreen = !actions;
  // Editing in Studio: rows and cells show they can be clicked
  const isHoverable = !isFullscreen && !isEmbedded;
  // The first row's badges have no room above it with the header hidden
  const isBadgeInside = (rowIndex: number) =>
    rowIndex === 0 && !!card.isHeaderHidden;
  // Its tabs (name, drag chip) as far in from its outline's corner as a
  // block's are on the canvas, its outline drawn in from its edges
  const cellTabLeft = (row: Studio.CompositeRow) =>
    (row.hasDividers ? 0 : CELL_OUTLINE_INSET_X) + CANVAS_TAB_INSET_X;
  const rowTabLeft = (row: Studio.CompositeRow) =>
    (row.hasDividers ? 0 : ROW_OUTLINE_INSET_X) + CANVAS_TAB_INSET_X;
  // As are its cells' floating menus, over their outlines
  const menuPlacementFor = ({
    row,
    rowIndex,
    elementIndex,
  }: {
    row: Studio.CompositeRow;
    rowIndex: number;
    elementIndex: number;
  }): MenuPlacement => ({
    isInside: isBadgeInside(rowIndex),
    isAtBlockSide: elementIndex === row.elements.length - 1,
    insetX: row.hasDividers ? 0 : CELL_OUTLINE_INSET_X,
  });
  // Selected in Studio, the block shows its layout: its rows and cells
  // outlined. Only then are its rows
  // and cells hovered, named and given their menus, and its rows selected:
  // until then, a press selects (or drags) the block, or the element
  // pressed, if any.
  const isEditingLayout = isHoverable && isSelected;
  const areChildMenusShown = !isHoverable || isEditingLayout;
  const rows = useMemo(() => card.rows ?? [], [card.rows]);
  // Editing in Studio: what each element's ⋮ menu does to it. A copy is
  // selected, as a duplicated block is.
  const editActionsFor = (
    element: Studio.CompositeElement,
  ): ElementEditActions | undefined =>
    isHoverable && actions
      ? {
          onEdit: () => onSelectElement(element.id),
          onOpenSettings: (section) =>
            actions.onOpenSettings(section, element.id),
          onDuplicate: () => {
            const result = duplicateElement({ rows, elementId: element.id });
            onRowsChange(result.rows);
            if (result.elementId) {
              onSelectElement(result.elementId);
            }
          },
          onDetach: onDetach
            ? () => onDetach({ elementId: element.id })
            : undefined,
          onRemove: () =>
            onRowsChange(removeElement({ rows, elementId: element.id })),
        }
      : undefined;
  // Large Numbers follow the block's height as well as its size, so a
  // shorter block's Numbers leave its chart room
  const {
    ref: rowsRef,
    height: rowsHeight,
    width: rowsWidth,
  } = useElementSize<HTMLDivElement>();
  const largeFontSize = Math.round(
    Math.max(
      20,
      Math.min(
        valueFontSize,
        44,
        rowsHeight > 0 ? rowsHeight * NUMBER_FONT_PER_HEIGHT : Infinity,
      ),
    ),
  );
  const mediumFontSize = Math.round(largeFontSize * 0.6);
  // Resizing a cell by its handles, previewed as it's dragged and saved on
  // release
  const blockRef = useRef<HTMLDivElement>(null);
  // The block as the resize started (see growRowsToFit): its rows' height,
  // a share of it, a grid row's height, and each row's height, which those
  // without a set height keep. Taken then, so the block growing as it's
  // dragged doesn't change them.
  const resizeStartRef = useRef<
    Omit<Parameters<typeof growRowsToFit>[0], 'rows'>
  >({ rowsHeight: 0, percentPx: 0, gridRowPx: 0, rowHeightsPx: {} });
  const captureResizeStart = () => {
    const block = blockRef.current;
    const rowsEl = rowsRef.current;
    if (!block || !rowsEl) {
      return;
    }
    const rowEls = Array.from(rowsEl.children).filter((child) =>
      child.classList.contains(ROW_CLASS),
    );
    // Measured as the rows below are, so they agree
    const startRowsHeight = rowsEl.getBoundingClientRect().height;
    resizeStartRef.current = {
      rowsHeight: startRowsHeight,
      percentPx: (startRowsHeight * getRowsScale(rows)) / 100,
      gridRowPx:
        card.layout.h > 0
          ? (block.getBoundingClientRect().height + CANVAS_ITEM_MARGIN) /
            card.layout.h
          : 0,
      rowHeightsPx: Object.fromEntries(
        rows.map((r, i) => [
          r.id,
          rowEls[i] ? rowEls[i].getBoundingClientRect().height : 0,
        ]),
      ),
    };
  };
  // A row being resized by its bottom edge, and how far it's been dragged
  const [rowResize, setRowResize] = useState<
    { row: Studio.CompositeRow; deltaPx: number } | undefined
  >(undefined);
  const resizeRow = ({
    row,
    deltaPx,
  }: {
    row: Studio.CompositeRow;
    deltaPx: number;
  }) =>
    resizeRowHeight({
      rows,
      rowId: row.id,
      deltaPx,
      stepPx: resizeStartRef.current.gridRowPx * ROW_RESIZE_GRID_ROWS,
      minPx: rowHasChart(row) ? CHART_ROW_MIN_PX : ROW_MIN_PX,
      ...resizeStartRef.current,
    });
  // Dragged taller than the block has room for, it grows to fit (on the
  // canvas only)
  const fitToBlock = useCallback(
    (resizedRows: Studio.CompositeRow[]) =>
      onRowsGrow
        ? growRowsToFit({ rows: resizedRows, ...resizeStartRef.current })
        : { rows: resizedRows, gridRows: 0 },
    [onRowsGrow],
  );
  const onResized = useCallback(
    (resized: ResizePreview) => {
      const grown = fitToBlock(
        setElementLayouts({ rows, layouts: resized.layouts }),
      );
      if (onRowsGrow && grown.gridRows > 0) {
        onRowsGrow(grown);
      } else {
        onRowsChange(grown.rows);
      }
    },
    [fitToBlock, onRowsChange, onRowsGrow, rows],
  );
  const { preview: resizing, startResize } = useCompositeResize({
    layout: card.layout,
    onResized,
  });
  // As laid out, with a resize under way: the block grown below its cell
  // while it's dragged taller than it has room for, as it will be
  const resized = rowResize
    ? resizeRow(rowResize)
    : resizing
      ? fitToBlock(setElementLayouts({ rows, layouts: resizing.layouts }))
      : { rows, gridRows: 0 };
  const laidOutRows = resized.rows;
  const previewGrowthPx = resized.gridRows * resizeStartRef.current.gridRowPx;
  const laidOutById = useMemo(
    () => new Map(laidOutRows.flatMap((r) => r.elements.map((e) => [e.id, e]))),
    [laidOutRows],
  );
  const rowSizing = useMemo(() => getRowSizing(laidOutRows), [laidOutRows]);
  // A share of the block's height in px (see getRowsScale)
  const percentPx = (rowsHeight * getRowsScale(laidOutRows)) / 100;
  // A cell's handles resize just it (see useCompositeResize)
  const resizeHandlesFor = ({
    row,
    element,
    elementIndex,
  }: {
    row: Studio.CompositeRow;
    element: Studio.CompositeElement;
    elementIndex: number;
  }) => (
    <CellResizeHandles
      edges={CELL_RESIZE_EDGES}
      insetX={row.hasDividers ? 0 : CELL_OUTLINE_INSET_X}
      isSelected={isSelected && element.id === selectedElementId}
      sizes={
        resizing && resizing.elementId === element.id
          ? resizing.sizes
          : undefined
      }
      onStart={(e, edge) => {
        const block = blockRef.current;
        const rowsEl = rowsRef.current;
        const rowEl = e.currentTarget.closest(`.${ROW_CLASS}`);
        if (!block || !rowsEl || !(rowEl instanceof HTMLElement)) {
          return;
        }
        captureResizeStart();
        startResize({
          e,
          edge,
          block,
          rowsEl,
          rowEl,
          cells: Array.from(rowEl.children).filter(
            (child): child is HTMLElement =>
              child instanceof HTMLElement &&
              child.classList.contains(ELEMENT_CELL_CLASS),
          ),
          elements: row.elements,
          index: elementIndex,
          gap: row.hasDividers ? 0 : ROW_GAP,
          // Only resized in Studio
          rowPaddingX: row.hasDividers ? 0 : ROW_STUDIO_PADDING_X,
          rowsScale: getRowsScale(rows),
        });
      }}
      // Its row sizes automatically again, across or down (or both, from
      // a corner)
      onReset={(edge) => {
        const isAcross = edge.includes('e') || edge.includes('w');
        const isDown = edge.includes('n') || edge.includes('s');
        onRowsChange(
          setElementLayouts({
            rows,
            layouts: Object.fromEntries(
              row.elements.map((e) => [
                e.id,
                {
                  ...(isAcross
                    ? { widthPercent: undefined, offsetPercent: undefined }
                    : {}),
                  ...(isDown
                    ? { heightPercent: undefined, offsetTopPercent: undefined }
                    : {}),
                },
              ]),
            ),
          }),
        );
      }}
      testId={`studio-composite-resize-${element.id}`}
    />
  );
  // A cell's size and place in its row, as laid out (see Element)
  const layoutOf = ({
    row,
    element,
    elementIndex,
  }: {
    row: Studio.CompositeRow;
    element: Studio.CompositeElement;
    elementIndex: number;
  }): CellLayout => {
    const laidOut = laidOutById.get(element.id) ?? element;
    const toPx = (percent: number | undefined) =>
      percent === undefined || percentPx <= 0 ? undefined : percent * percentPx;
    return {
      widthPercent: laidOut.widthPercent,
      gapShare: row.hasDividers
        ? 0
        : (ROW_GAP * (row.elements.length - 1)) / row.elements.length,
      offsetPercent: laidOut.offsetPercent,
      heightPx: toPx(laidOut.heightPercent),
      offsetTopPx:
        laidOut.heightPercent === undefined
          ? undefined
          : toPx(laidOut.offsetTopPercent),
    };
  };
  // How far each Number has had to shrink to fit (a share of its largest
  // size), so a tight row's Numbers can shrink together
  const [numberScales, setNumberScales] = useState<Record<string, number>>({});
  const onNumberFit = useCallback(
    ({ elementId, scale }: { elementId: string; scale: number }) =>
      setNumberScales((current) =>
        current[elementId] === scale
          ? current
          : { ...current, [elementId]: scale },
      ),
    [],
  );
  // A plain row too narrow for its Numbers' natural widths splits evenly,
  // its Numbers drawn at one scale
  const rowFits = useMemo(
    () =>
      (card.rows ?? []).map((row) => {
        const numbers = row.elements.filter(isNumberElement);
        // Within its sides' padding in Studio
        const rowWidth = isHoverable
          ? rowsWidth - 2 * ROW_STUDIO_PADDING_X
          : rowsWidth;
        const isTight =
          !row.hasDividers &&
          isRowTight({ rowWidth, numberCount: numbers.length });
        return {
          isTight,
          scale: isTight
            ? getRowScale(numbers.map((e) => numberScales[e.id]))
            : 1,
        };
      }),
    [numberScales, card.rows, rowsWidth, isHoverable],
  );
  // A selected Text element is being typed into
  const selectedElement = isSelected
    ? findElement({ rows, elementId: selectedElementId })
    : undefined;
  const isEditingText = !!selectedElement && isTextElement(selectedElement);
  // Any of the block's metrics can be named in a Number's subtext
  const blockMetricIds = useMemo(() => getCardMetricIds(card), [card]);
  // Progress bars fill with the accent, or the default one without it
  const progressColor =
    toAccentColor(card.accent) ?? toAccentCss(DEFAULT_ACCENT);

  // Rearranging: the element being dragged by its handle, and the cell side
  // it would be dropped on. Only within this block.
  const [draggingElementId, setDraggingElementId] = useState<
    string | undefined
  >(undefined);
  const [dropIndicator, setDropIndicator] = useState<
    { elementId: string; side: DropSide } | undefined
  >(undefined);
  // Reordering rows: the row being dragged by its handle, and the side of
  // the row it would be dropped on
  const [draggingRowId, setDraggingRowId] = useState<string | undefined>(
    undefined,
  );
  const [rowDropIndicator, setRowDropIndicator] = useState<
    { rowId: string; side: DropSide } | undefined
  >(undefined);
  const endRowDrag = () => {
    setDraggingRowId(undefined);
    setRowDropIndicator(undefined);
  };
  const getRowDropSide = (e: React.DragEvent<HTMLElement>): DropSide => {
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
  };
  // Selected in Studio, a block with rows to reorder has them dragged by
  // their bodies (a native drag, as its cells' are), the row its own drag
  // image. A drag from a cell moves the cell instead, unless the row's
  // selected; never from a press on a control in the row (a menu, the
  // toolbar, a chart taking clicks), nor a row with Text being typed in.
  const isRowPressOnControlRef = useRef(false);
  const rowDragHandlersFor = (row: Studio.CompositeRow) =>
    isEditingLayout &&
    rows.length > 1 &&
    !(
      isEditingText &&
      row.elements.some((element) => element.id === selectedElementId)
    )
      ? {
          draggable: true,
          onMouseDownCapture: (e: React.MouseEvent<HTMLDivElement>) => {
            const control = (e.target as HTMLElement).closest(
              `.${NO_DRAG_CLASS}, .${ROW_TOOLBAR_CLASS}`,
            );
            isRowPressOnControlRef.current =
              !!control && e.currentTarget.contains(control);
          },
          onDragStart: (e: React.DragEvent<HTMLDivElement>) => {
            if (isRowPressOnControlRef.current) {
              e.preventDefault();
              return;
            }
            e.stopPropagation();
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', row.id);
            setDraggingRowId(row.id);
          },
          onDragEnd: endRowDrag,
        }
      : {};
  // Drop handlers for a row, while another row is dragged
  const rowDropHandlersFor = (row: Studio.CompositeRow) => {
    if (!draggingRowId || draggingRowId === row.id) {
      return {};
    }
    return {
      onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        const side = getRowDropSide(e);
        setRowDropIndicator((prev) =>
          prev && prev.rowId === row.id && prev.side === side
            ? prev
            : { rowId: row.id, side },
        );
      },
      onDragLeave: (e: React.DragEvent<HTMLDivElement>) => {
        const next = e.relatedTarget as Node | null;
        if (!next || !e.currentTarget.contains(next)) {
          setRowDropIndicator((prev) =>
            prev && prev.rowId === row.id ? undefined : prev,
          );
        }
      },
      onDrop: (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        // After the row is before whatever follows it, leaving out the
        // dragged row itself
        const others = rows.filter((r) => r.id !== draggingRowId);
        const next = others[others.findIndex((r) => r.id === row.id) + 1];
        const moved = moveRowBefore({
          rows,
          rowId: draggingRowId,
          beforeRowId:
            getRowDropSide(e) === 'before' ? row.id : next && next.id,
        });
        endRowDrag();
        onRowsChange(moved);
      },
    };
  };

  const endElementDrag = () => {
    setDraggingElementId(undefined);
    setDropIndicator(undefined);
  };

  // Selected in Studio, the block's children are dragged by their bodies to
  // another place in it (a native drag, like the sidebar's, rather than the
  // grid's), the cell its own drag image. Not a Text being typed in, nor
  // from a press on a child's own controls. A selected row's cells move
  // with it instead (see rowDragHandlersFor).
  const isPressOnControlRef = useRef(false);
  const dragHandlersFor = ({
    row,
    element,
  }: {
    row: Studio.CompositeRow;
    element: Studio.CompositeElement;
  }) =>
    isEditingLayout &&
    !(isEditingText && element.id === selectedElementId) &&
    !(isSelected && row.id === selectedRowId)
      ? {
          draggable: true,
          onDragStart: (e: React.DragEvent<HTMLDivElement>) => {
            if (isPressOnControlRef.current) {
              e.preventDefault();
              return;
            }
            e.stopPropagation();
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', element.id);
            setDraggingElementId(element.id);
          },
          onDragEnd: endElementDrag,
        }
      : {};

  // A row takes the dragged element if it's already there or has room
  const canDropInto = (row: Studio.CompositeRow): boolean =>
    !!draggingElementId &&
    (row.elements.some((e) => e.id === draggingElementId) ||
      row.elements.length < MAX_ELEMENTS_PER_ROW);

  // Where a drag could put an element, shown while it's dragged (see
  // DropSpotBar): before each element of a row, and after its last. Moving
  // an element, in rows it could go in, leaving out either side of it (where
  // it is already); a sidebar metric or vis type, in rows with room.
  // Only the block a drag is over shows where it could go
  const isLibraryDrag = !!addElementDrop;
  // The row a dragged gadget would land in: one of the block's own, by the
  // cell it's over; one from the sidebar, as the canvas places it
  const dropRowId = draggingElementId
    ? dropIndicator &&
      rows.find((r) => r.elements.some((e) => e.id === dropIndicator.elementId))
        ?.id
    : addElementDrop && addElementDrop.rowId;
  const dropSpotsFor = (row: Studio.CompositeRow): DropSpot[] => {
    const ids = row.elements.map((e) => e.id);
    const last = ids[ids.length - 1];
    const spots: DropSpot[] = [
      ...ids.map((elementId) => ({ elementId, side: 'before' as const })),
      ...(last ? [{ elementId: last, side: 'after' as const }] : []),
    ];
    if (draggingElementId) {
      if (!canDropInto(row)) {
        return [];
      }
      const at = ids.indexOf(draggingElementId);
      return at === -1
        ? spots
        : spots.filter(
            (spot) =>
              spot.elementId !== draggingElementId &&
              !(spot.side === 'before' && spot.elementId === ids[at + 1]),
          );
    }
    return isLibraryDrag && ids.length < MAX_ELEMENTS_PER_ROW ? spots : [];
  };
  // The spot the drag is over: after an element is before the next one
  const activeDropSpotFor = (
    row: Studio.CompositeRow,
  ): DropSpot | undefined => {
    const over = draggingElementId
      ? dropIndicator
      : addElementDrop && addElementDrop.rowId === row.id
        ? addElementDrop.indicator
        : undefined;
    const ids = row.elements.map((e) => e.id);
    if (!over || !ids.includes(over.elementId)) {
      return undefined;
    }
    const next = ids[ids.indexOf(over.elementId) + 1];
    return over.side === 'after' && next
      ? { elementId: next, side: 'before' }
      : over;
  };

  // A sidebar drag's preview in the row it would go in (see DropPreviewCell):
  // before the element at its index (or after the row's last). Not in a full
  // row, whose drags go in a new row.
  type ShownDropPreview = {
    key: string;
    rowId: string;
    at: number;
    isFill: boolean;
  };
  const shownPreview: ShownDropPreview | undefined = (() => {
    const row =
      !draggingElementId && addElementDrop && addElementDrop.rowId
        ? rows.find((r) => r.id === addElementDrop.rowId)
        : undefined;
    const spot = row && activeDropSpotFor(row);
    if (!row || !spot || row.elements.length >= MAX_ELEMENTS_PER_ROW) {
      return undefined;
    }
    const ids = row.elements.map((e) => e.id);
    return {
      key: `${row.id}:${spot.elementId}:${spot.side}`,
      rowId: row.id,
      at: spot.side === 'before' ? ids.indexOf(spot.elementId) : ids.length,
      isFill:
        !(addElementDrop && addElementDrop.isNumber) ||
        !!row.hasDividers ||
        rowFits[rows.indexOf(row)].isTight,
    };
  })();
  // Moved away from (not dropped, which changes the rows: the gadget takes
  // its place), it stays a moment to shrink back out
  const [closingPreview, setClosingPreview] = useState<
    ShownDropPreview | undefined
  >(undefined);
  const lastPreviewRef = useRef<{
    preview: ShownDropPreview | undefined;
    rows: Studio.CompositeRow[];
  }>({ preview: undefined, rows });
  const closingTimerRef = useRef<number | undefined>(undefined);
  const shownPreviewKey = shownPreview && shownPreview.key;
  useEffect(() => {
    const last = lastPreviewRef.current;
    lastPreviewRef.current = { preview: shownPreview, rows };
    if (!last.preview || last.preview.key === shownPreviewKey) {
      return;
    }
    window.clearTimeout(closingTimerRef.current);
    if (last.rows !== rows) {
      setClosingPreview(undefined);
      return;
    }
    const closing = last.preview;
    setClosingPreview(closing);
    closingTimerRef.current = window.setTimeout(
      () => setClosingPreview((c) => (c === closing ? undefined : c)),
      DROP_PREVIEW_MS,
    );
    // Only as the shown preview changes, or the rows (see above)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownPreviewKey, rows]);
  useEffect(() => () => window.clearTimeout(closingTimerRef.current), []);
  const isRowPreviewed = (row: Studio.CompositeRow): boolean =>
    !!shownPreview && shownPreview.rowId === row.id;
  const renderDropPreview = ({
    row,
    at,
  }: {
    row: Studio.CompositeRow;
    at: number;
  }) => {
    const preview =
      shownPreview && shownPreview.rowId === row.id && shownPreview.at === at
        ? shownPreview
        : undefined;
    const closing =
      !preview &&
      closingPreview &&
      closingPreview.rowId === row.id &&
      closingPreview.at === at
        ? closingPreview
        : undefined;
    const shown = preview || closing;
    if (!shown) {
      return null;
    }
    // The gap it collapses: after it at the row's start, else before it
    const collapse = row.hasDividers ? 0 : -ROW_GAP;
    return (
      <DropPreviewCell
        key={closing ? 'drop-preview-closing' : 'drop-preview'}
        $isFill={shown.isFill}
        $isClosing={!!closing}
        style={
          {
            [at === 0 ? '--collapse-right' : '--collapse-left']:
              `${collapse}px`,
          } as React.CSSProperties
        }
        data-testid={`studio-composite-drop-preview-${row.id}-${at}`}
        data-closing={!!closing}
      >
        {preview && (
          <DropSpotLabel data-testid={`studio-composite-drop-label-${row.id}`}>
            Add To Row
          </DropSpotLabel>
        )}
        <DropPreviewIcon>
          <Icon icon="add" width={16} height={16} color="currentColor" />
        </DropPreviewIcon>
      </DropPreviewCell>
    );
  };

  // Drop handlers for an element's cell, while another element is dragged.
  // Sidebar drags pass through to the canvas.
  const dropHandlersFor = ({
    row,
    element,
  }: {
    row: Studio.CompositeRow;
    element: Studio.CompositeElement;
  }) => {
    if (
      !draggingElementId ||
      draggingElementId === element.id ||
      !canDropInto(row)
    ) {
      return {};
    }
    return {
      onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        const side = getDropSide(e);
        setDropIndicator((prev) =>
          prev && prev.elementId === element.id && prev.side === side
            ? prev
            : { elementId: element.id, side },
        );
      },
      onDragLeave: (e: React.DragEvent<HTMLDivElement>) => {
        const next = e.relatedTarget as Node | null;
        if (!next || !e.currentTarget.contains(next)) {
          setDropIndicator((prev) =>
            prev && prev.elementId === element.id ? undefined : prev,
          );
        }
      },
      onDrop: (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        // After the cell is before whatever follows it, leaving out the
        // dragged element itself
        const others = row.elements.filter((x) => x.id !== draggingElementId);
        const next = others[others.findIndex((x) => x.id === element.id) + 1];
        const moved = moveElement({
          rows,
          elementId: draggingElementId,
          toRowId: row.id,
          beforeElementId:
            getDropSide(e) === 'before' ? element.id : next && next.id,
        });
        endElementDrag();
        // Moved, not selected, so the settings stay as they were
        if (moved) {
          onRowsChange(moved);
        }
      },
    };
  };

  return (
    <>
      <CardShell
        ref={blockRef}
        $isSelected={isSelected || isDropTarget}
        $isActive={isSelected}
        $isEditing={isEditingText}
        $isEmbedded={isEmbedded}
        // Selected, it's being edited: pressed and dragged, its children
        // move rather than the block (as a selected gadget's chart takes the
        // pointer). Unselected, it drags as any block does.
        className={isEditingLayout ? NO_DRAG_CLASS : undefined}
        style={{
          ...(isEditingLayout ? { cursor: 'default' } : {}),
          // Grown (or shrunk) over what's below while a resize needs it,
          // until the release resizes its cell (see fitToBlock)
          ...(previewGrowthPx !== 0
            ? { height: `calc(100% + ${previewGrowthPx}px)` }
            : {}),
        }}
        {...{ [STUDIO_COMPOSITE_CARD_ATTR]: card.id }}
        data-testid={`studio-card-${card.id}`}
      >
        {card.isHeaderHidden ? (
          actions ? (
            <OverlayActions
              cardId={card.id}
              actions={actions}
              dates={{
                hasOwnDates: !!card.dateSettings,
                hasFilters: (card.filters ?? []).length > 0,
              }}
            />
          ) : (
            viewMenu && (
              <OverlayViewMenu cardId={card.id}>{viewMenu}</OverlayViewMenu>
            )
          )
        ) : (
          <HeaderHitArea
            onMouseDown={() => selectOnRelease(onSelectBlock)}
            data-testid={`studio-composite-header-${card.id}`}
          >
            <CardHeader
              cardId={card.id}
              title={card.name || 'Untitled'}
              description={
                card.isDescriptionShown ? card.description : undefined
              }
              accentColor={toAccentColor(card.accent)}
              icon={
                card.headerIcon && {
                  name: card.headerIcon,
                  color:
                    toAccentColor(card.accent) ?? 'var(--color-icon-secondary)',
                }
              }
              hasOwnDates={!!card.dateSettings}
              hasFilters={(card.filters ?? []).length > 0}
              actions={actions}
              viewMenu={viewMenu}
            />
          </HeaderHitArea>
        )}
        <CompositeLegendProvider rows={rows}>
          <Rows
            ref={rowsRef}
            $isHeaderHidden={!!card.isHeaderHidden}
            data-testid={`studio-composite-rows-${card.id}`}
          >
            {rows.map((row, rowIndex) => (
              <Row
                key={row.id}
                $sizing={rowSizing[rowIndex]}
                $hasChart={rowHasChart(row)}
                $hasDividers={!!row.hasDividers}
                $isHoverable={isEditingLayout}
                $isInStudio={isHoverable}
                $isOutlined={isEditingLayout}
                $isRowSelected={isSelected && row.id === selectedRowId}
                $isDropTarget={row.id === dropRowId}
                data-drop-target={row.id === dropRowId}
                className={ROW_CLASS}
                // Its elements, toolbar and resize handles claim their own
                // presses first; the rest of the row selects it
                onMouseDown={(e) => {
                  const target = e.target as HTMLElement;
                  if (
                    isEditingLayout &&
                    onSelectRow &&
                    !target.closest(`.${ROW_TOOLBAR_CLASS}`)
                  ) {
                    selectOnRelease(() => onSelectRow(row.id));
                  }
                }}
                {...rowDragHandlersFor(row)}
                {...rowDropHandlersFor(row)}
                {...{ [STUDIO_ROW_ID_ATTR]: row.id }}
                data-testid={`studio-composite-row-${row.id}`}
              >
                {/* The row's own handle: selects it, and moves it among
                    the rows */}
                {isEditingLayout && (
                  <RowHandle
                    rowId={row.id}
                    left={row.hasDividers ? 2 : ROW_OUTLINE_INSET_X - 7}
                    isDraggable={rows.length > 1}
                  />
                )}
                {/* Pushes the rows below (and the blocks below the block)
                    down as it's dragged taller, on the canvas only */}
                {isEditingLayout && onRowsGrow && (
                  <RowResizeHandle
                    rowId={row.id}
                    onStart={() => {
                      captureResizeStart();
                      setRowResize({ row, deltaPx: 0 });
                    }}
                    onMove={(deltaPx) => setRowResize({ row, deltaPx })}
                    onEnd={(deltaPx) => {
                      const result = resizeRow({ row, deltaPx });
                      setRowResize(undefined);
                      if (result.gridRows !== 0) {
                        onRowsGrow(result);
                      }
                    }}
                    onReset={() =>
                      onRowsChange(
                        setElementLayouts({
                          rows: setRowHeight({
                            rows,
                            rowId: row.id,
                            heightPercent: undefined,
                          }),
                          layouts: Object.fromEntries(
                            row.elements.map((e) => [
                              e.id,
                              {
                                heightPercent: undefined,
                                offsetTopPercent: undefined,
                              },
                            ]),
                          ),
                        }),
                      )
                    }
                  />
                )}
                {rowDropIndicator && rowDropIndicator.rowId === row.id && (
                  <RowDropBar
                    className={ROW_DROP_BAR_CLASS}
                    $side={rowDropIndicator.side}
                    $inset={row.hasDividers ? 0 : ROW_OUTLINE_INSET_X}
                    data-testid={`studio-composite-row-drop-${row.id}`}
                  />
                )}
                {row.elements.flatMap((element, elementIndex) => [
                  renderDropPreview({ row, at: elementIndex }),
                  <ElementCell
                    key={element.id}
                    element={element}
                    isShadedWhenLinked={!isHoverable}
                    $isFill={
                      !isNumberElement(element) ||
                      !!row.hasDividers ||
                      rowFits[rowIndex].isTight
                    }
                    $isDivided={!!row.hasDividers}
                    $isChart={
                      !isNumberElement(element) && !isTextElement(element)
                    }
                    $isSelected={isSelected && element.id === selectedElementId}
                    $isHoverable={isEditingLayout}
                    $isOutlined={isEditingLayout}
                    $layout={layoutOf({ row, element, elementIndex })}
                    onMouseDown={(e) => {
                      // A press on its own controls (its menu, resize
                      // handles, a chart taking clicks) doesn't drag it
                      const control = (e.target as HTMLElement).closest(
                        `.${NO_DRAG_CLASS}`,
                      );
                      isPressOnControlRef.current =
                        !!control && e.currentTarget.contains(control);
                      // Selected straight away, even before its block is
                      // (which it selects too)
                      selectOnRelease(() => onSelectElement(element.id));
                    }}
                    {...dragHandlersFor({ row, element })}
                    {...dropHandlersFor({ row, element })}
                    data-testid={`studio-composite-element-${element.id}`}
                  >
                    {dropSpotsFor(row)
                      .filter((spot) => spot.elementId === element.id)
                      .map((spot) => (
                        <DropSpotBar
                          key={spot.side}
                          $side={spot.side}
                          $offset={
                            // At the row's ends, just outside it
                            (spot.side === 'before' && elementIndex === 0) ||
                            spot.side === 'after'
                              ? 2
                              : (row.hasDividers ? 0 : ROW_GAP) / 2
                          }
                          $isActive={isSameSpot(spot, activeDropSpotFor(row))}
                          // The preview stands in for it
                          $isHidden={
                            isRowPreviewed(row) &&
                            isSameSpot(spot, activeDropSpotFor(row))
                          }
                          data-testid={`studio-composite-drop-spot-${element.id}-${spot.side}`}
                          data-active={isSameSpot(spot, activeDropSpotFor(row))}
                        >
                          <Icon
                            icon="add"
                            width={8}
                            height={8}
                            color="currentColor"
                          />
                          {isSameSpot(spot, activeDropSpotFor(row)) &&
                            !isRowPreviewed(row) && (
                              <DropSpotLabel
                                data-testid={`studio-composite-drop-label-${row.id}`}
                              >
                                Add To Row
                              </DropSpotLabel>
                            )}
                        </DropSpotBar>
                      ))}
                    {/* On hover, once the block is selected: one that isn't
                        selected shows it can be dragged or selected, as
                        the canvas's blocks do; the selected one is named.
                        Either hides its row's name. */}
                    {isEditingLayout &&
                      !(isSelected && element.id === selectedElementId) && (
                        <DragChip
                          className={ELEMENT_BADGE_CLASS}
                          left={cellTabLeft(row)}
                          isInside={isBadgeInside(rowIndex)}
                          testId={`studio-composite-element-drag-chip-${element.id}`}
                        />
                      )}
                    {isEditingLayout &&
                      isSelected &&
                      element.id === selectedElementId && (
                        <HoverBadge
                          className={ELEMENT_BADGE_CLASS}
                          $isInside={isBadgeInside(rowIndex)}
                          $left={cellTabLeft(row)}
                          aria-hidden
                          data-testid={`studio-composite-element-badge-${element.id}`}
                        >
                          {/* It's what's being edited, its block only
                              selected (see StudioCanvas's tab) */}
                          Editing
                        </HoverBadge>
                      )}
                    {/* A chart's menu is drawn under its scopes instead
                        (see ChartElement) */}
                    {areChildMenusShown &&
                      (isNumberElement(element) || isTextElement(element)) && (
                        <ElementMenu
                          element={element}
                          name=""
                          w={card.layout.w}
                          h={card.layout.h}
                          hasPopup={false}
                          editActions={editActionsFor(element)}
                          {...menuPlacementFor({ row, rowIndex, elementIndex })}
                        />
                      )}
                    {/* On its edges and corners, as a block's on the
                        canvas: dragged, they resize it against its
                        neighbours; double-clicked, it sizes automatically
                        again */}
                    {isEditingLayout &&
                      resizeHandlesFor({ row, element, elementIndex })}
                    <DateScope dateSettings={element.dateSettings}>
                      {/* Elements get the block's filters, then their own */}
                      <FilterScope
                        filters={[
                          ...(card.filters ?? []),
                          ...(element.filters ?? []),
                        ]}
                        // A dashboard chart's run-time filters. Without any
                        // it takes those around it (see FilterScope).
                        drillDowns={
                          element.drillDowns && element.drillDowns.length > 0
                            ? element.drillDowns
                            : undefined
                        }
                      >
                        {isTextElement(element) ? (
                          <DynamicStudioText
                            id={element.id}
                            text={element.text}
                            metricIds={blockMetricIds}
                            isEditing={
                              isSelected && element.id === selectedElementId
                            }
                            onChange={(text) =>
                              onRowsChange(
                                updateElement({
                                  rows,
                                  element: { ...element, text },
                                }),
                              )
                            }
                          />
                        ) : isNumberElement(element) ? (
                          <NumberElement
                            element={element}
                            isCentred={isRowStretched(rowSizing[rowIndex])}
                            blockMetricIds={blockMetricIds}
                            progressColor={progressColor}
                            fontSize={
                              element.size === 'medium'
                                ? mediumFontSize
                                : largeFontSize
                            }
                            rowScale={rowFits[rowIndex].scale}
                            isSetupHintShown={
                              isHoverable &&
                              !(isSelected && element.id === selectedElementId)
                            }
                            onFit={onNumberFit}
                          />
                        ) : (
                          <ChartElement
                            element={element}
                            w={card.layout.w}
                            h={card.layout.h}
                            // Only the selected chart element takes the pointer
                            // (tooltips), not every chart in the selected block.
                            // In fullscreen they all do.
                            isInteractive={
                              isFullscreen ||
                              (isSelected && element.id === selectedElementId)
                            }
                            hasContextMenu={hasChartMenu}
                            isOnView={isEmbedded}
                            blockFilters={card.filters}
                            actions={actions}
                            editActions={editActionsFor(element)}
                            isMenuShown={areChildMenusShown}
                            isSetupHintShown={
                              isHoverable &&
                              !(isSelected && element.id === selectedElementId)
                            }
                            menuPlacement={menuPlacementFor({
                              row,
                              rowIndex,
                              elementIndex,
                            })}
                          />
                        )}
                      </FilterScope>
                    </DateScope>
                  </ElementCell>,
                ])}
                {renderDropPreview({ row, at: row.elements.length })}
                {/* On hover, as its cells' are: one that isn't selected
                    shows it can be dragged (by its handle, with other rows
                    to go between) or selected; otherwise it's named */}
                {isEditingLayout &&
                  rows.length > 1 &&
                  !(isSelected && row.id === selectedRowId) && (
                    <DragChip
                      className={ROW_BADGE_CLASS}
                      left={rowTabLeft(row)}
                      label="Drag or select row to edit"
                      isInside={isBadgeInside(rowIndex)}
                      testId={`studio-composite-row-drag-chip-${row.id}`}
                    />
                  )}
                {isEditingLayout &&
                  (rows.length === 1 ||
                    (isSelected && row.id === selectedRowId)) && (
                    <HoverBadge
                      className={ROW_BADGE_CLASS}
                      $isInside={isBadgeInside(rowIndex)}
                      $left={rowTabLeft(row)}
                      aria-hidden
                      data-testid={`studio-composite-row-badge-${row.id}`}
                    >
                      {isSelected && row.id === selectedRowId
                        ? 'Editing'
                        : `Row ${rowIndex + 1}`}
                    </HoverBadge>
                  )}
                {isSelected && row.id === selectedRowId && (
                  <RowToolbar
                    rows={rows}
                    row={row}
                    isInside={isBadgeInside(rowIndex)}
                    onRowsChange={onRowsChange}
                  />
                )}
              </Row>
            ))}
            {rows.length === 0 && isHoverable && (
              <EmptyLayout
                $isHeaderHidden={!!card.isHeaderHidden}
                $isOutlined={isEditingLayout}
                $isDropSpot={isLibraryDrag}
                $isActive={!!addElementDrop && !!addElementDrop.isNewRow}
                {...{ [STUDIO_ADD_ROW_CARD_ATTR]: card.id }}
                data-testid={`studio-composite-empty-${card.id}`}
              >
                <Typography.Body
                  type="Body 13"
                  color="var(--color-text-tertiary)"
                  textAlign="center"
                >
                  Drag multiple gadgets into this layout card
                </Typography.Body>
              </EmptyLayout>
            )}
          </Rows>
        </CompositeLegendProvider>
      </CardShell>
      {/* Over the new row slot, what dropping there does (outside the slot,
          which clips) */}
      {rows.length > 0 && !!addElementDrop && !!addElementDrop.isNewRow && (
        <NewRowLabelAnchor>
          <DropSpotLabel
            data-testid={`studio-composite-new-row-label-${card.id}`}
          >
            Create New Row
          </DropSpotLabel>
        </NewRowLabelAnchor>
      )}
      {isLibraryDrag && rows.length > 0 && (
        <FloatingAddRowSlot
          $isActive={!!addElementDrop && !!addElementDrop.isNewRow}
          {...{ [STUDIO_ADD_ROW_CARD_ATTR]: card.id }}
          data-testid={`studio-composite-add-row-${card.id}`}
        >
          <Icon icon="add" width={12} height={12} color="currentColor" />
          {SLOT_LABEL}
        </FloatingAddRowSlot>
      )}
    </>
  );
};

export default CompositeCard;
